import React, { useState, useEffect } from 'react';
import { Search, FileText, Calendar, Package, TrendingUp, AlertCircle, Eye, X, ArrowUpDown, ArrowUp, ArrowDown, Filter, Download, FileSpreadsheet, FileDown, User, CheckCircle, Clock, XCircle } from 'lucide-react';
import PurchaseOrderModal from "../Modal/PurchaseOrderModal";
import megalogo from '../Image/megalogo.png';

// Mock API_BASE_URL for demo
import API_BASE_URL from "../config";

// Load jsPDF dynamically
const loadJsPDF = async () => {
  if (window.jsPDF) return window.jsPDF;

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    script.onload = () => resolve(window.jspdf.jsPDF);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const PO_Reports = () => {
  const [poHeaders, setPoHeaders] = useState([]);
  const [filteredPOs, setFilteredPOs] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poDetails, setPoDetails] = useState([]);
  const [poApproval, setPoApproval] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState('poNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  const [companies, setCompanies] = useState({});
  const [vendors, setVendors] = useState({});


  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Company`);
      if (!res.ok) throw new Error("Failed to fetch companies");
      const data = await res.json();
      const companyMap = {};
      data.forEach((c) => {
        companyMap[c.id] = c.companyName || c.CompanyName || "Unknown";
      });
      setCompanies(companyMap);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Vendor`);
      if (!res.ok) throw new Error("Failed to fetch vendors");
      const data = await res.json();
      const vendorMap = {};
      data.forEach((v) => {
        vendorMap[v.id] = v.vendorName || v.VendorName || "Unknown";
      });
      setVendors(vendorMap);
    } catch (error) {
      console.error(error);
    }
  };

  // Sa fetchOrders function, add user filtering


  // ---------------- USE EFFECTS ---------------- //

  useEffect(() => {
    fetchCompanies();
    fetchVendors();

  }, []);
  useEffect(() => {
    fetchPOHeaders();
  }, []);

  useEffect(() => {
    filterAndSortPOs();
  }, [searchTerm, statusFilter, poHeaders, sortField, sortDirection]);

const fetchPOHeaders = async () => {
  try {
    setLoading(true);

    // Fetch PO headers
    const response = await fetch(`${API_BASE_URL}api/T_PO_Header`);
    if (!response.ok) throw new Error("Failed to fetch PO headers");
    const data = await response.json();

    // Get logged-in user from localStorage
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      setPoHeaders([]);
      setFilteredPOs([]);
      return;
    }

    // Fetch user levels from API
    const levelRes = await fetch(`${API_BASE_URL}api/UserLevels`);
    const levels = await levelRes.json();

    // Find the level of the current user
    const userLevel = levels.find(
      l => l.isActive === 1 && l.userLevelName === user.positionId
    );

    if (!userLevel) {
      setPoHeaders([]);
      setFilteredPOs([]);
      return;
    }

    // Filter based on access level
    let filteredData = [];
    if (userLevel.accessViewAllData === true) {
      filteredData = data;
    } else if (userLevel.accessVIewUserOnly === true) {
      filteredData = data.filter(po => po.createdBy === user.userId);
    }

    setPoHeaders(filteredData);
    setFilteredPOs(filteredData);
  } catch (err) {
    console.error(err);
    setError(err.message);

    // Fallback mock data if API fails
    const mockData = [
      {
        id: 1,
        poNumber: "PO-2024-001",
        poCreatedDate: new Date(),
        deliveryDate: new Date(),
        shipTo_Location: "Main Warehouse",
        shipTo_ContactPerson: "John Doe",
        paymentTerms: "Net 30",
        status: 2
      }
    ];
    setPoHeaders(mockData);
    setFilteredPOs(mockData);
  } finally {
    setLoading(false);
  }
};


  const fetchPODetails = async (poHeaderId) => {
    try {
      setDetailsLoading(true);
      const response = await fetch(`${API_BASE_URL}api/T_Po_Details/get/${poHeaderId}`);
      if (!response.ok) throw new Error('Failed to fetch PO details');
      const data = await response.json();
      setPoDetails(data);
    } catch (err) {
      setError(err.message);
      // Mock data for demo
      setPoDetails([
        {
          id: 1,
          itemDescription: "Sample Item",
          qty: 10,
          uom: "PCS",
          unitPrice: 100,
          detailDiscount: 5,
          serveStatus: "Pending"
        }
      ]);
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchPOApproval = async (poHeaderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/T_PO_Approval/${poHeaderId}`);
      if (!response.ok) {
        setPoApproval(null);
        return;
      }
      const data = await response.json();
      setPoApproval(data);
    } catch (err) {
      setPoApproval(null);
    }
  };

  const filterAndSortPOs = () => {
    let filtered = [...poHeaders];

    if (searchTerm) {
      filtered = filtered.filter(po =>
        po.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.shipTo_Location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.shipTo_ContactPerson?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(po => po.status.toString() === statusFilter);
    }

    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'poCreatedDate' || sortField === 'deliveryDate') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });

    setFilteredPOs(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  // const handleViewDetails = async (po) => {
  //   setSelectedPO(po);
  //   setShowModal(true);
  //   setPoApproval(null);
  //   await fetchPODetails(po.id);
  //   await fetchPOApproval(po.id);
  // };

  const handleViewDetails = (po) => {
    setSelectedPO(po);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPO(null);
    setPoDetails([]);
    setPoApproval(null);
  };

  const exportToExcel = () => {
    const headers = ['PO Number', 'Created Date', 'Delivery Date', 'Location', 'Contact Person', 'Payment Terms', 'Status'];
    const data = filteredPOs.map(po => [
      po.poNumber,
      formatDate(po.poCreatedDate),
      formatDate(po.deliveryDate),
      po.shipTo_Location,
      po.shipTo_ContactPerson,
      po.paymentTerms,
      getStatusLabel(po.status)
    ]);

    let csv = headers.join(',') + '\n';
    data.forEach(row => {
      csv += row.map(cell => `"${cell}"`).join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PO_Reports_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const exportToPDF = () => {
    window.print();
  };

  const exportModalToExcel = (po, details) => {
    let csv = 'Purchase Order Details\n\n';
    csv += 'PO Number:,' + po.poNumber + '\n';
    csv += 'Created Date:,' + formatDate(po.poCreatedDate) + '\n';
    csv += 'Delivery Date:,' + formatDate(po.deliveryDate) + '\n';
    csv += 'Payment Terms:,' + po.paymentTerms + '\n';
    csv += 'Status:,' + getStatusLabel(po.status) + '\n\n';

    csv += 'Shipping Information\n';
    csv += 'Location:,' + po.shipTo_Location + '\n';
    csv += 'Address:,' + po.shipTo_Address1 + '\n';
    if (po.shipTo_Address2) csv += ',' + po.shipTo_Address2 + '\n';
    csv += 'Contact Person:,' + po.shipTo_ContactPerson + '\n';
    csv += 'Contact No:,' + po.shipTo_ContactNo + '\n\n';

    if (poApproval) {
      csv += 'Approval Information\n';
      csv += 'Approver ID:,User ' + poApproval.approverId + '\n';
      csv += 'User Level:,Level ' + poApproval.approverUserLevel + '\n';
      csv += 'Response:,' + ['Pending', 'Approved', 'Rejected'][poApproval.response] + '\n';
      csv += 'Response Date:,' + formatDateTime(poApproval.responseDateTime) + '\n';
      if (poApproval.responseRemarks) {
        csv += 'Remarks:,"' + poApproval.responseRemarks + '"\n';
      }
      csv += '\n';
    }

    csv += 'Order Items\n';
    csv += 'Item Description,Qty,UOM,Unit Price,Discount,Total,Status\n';

    details.forEach(item => {
      const itemTotal = item.qty * item.unitPrice * (1 - item.detailDiscount / 100);
      csv += `"${item.itemDescription}",${item.qty},${item.uom},${item.unitPrice},${item.detailDiscount}%,${itemTotal.toFixed(2)},${item.serveStatus}\n`;
    });

    const grandTotal = calculatePOTotal(details);
    csv += '\n,,,,,Grand Total:,' + grandTotal.toFixed(2) + '\n';

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PO_${po.poNumber}_Details_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const exportModalToPDF = async () => {
    try {
      const jsPDF = await loadJsPDF();
      const doc = new jsPDF();

      let yPos = 20;
      const pageWidth = doc.internal.pageSize.width;
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);

      // Title
      doc.setFillColor(37, 99, 235);
      doc.rect(0, 10, pageWidth, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.text('PURCHASE ORDER DETAILS', pageWidth / 2, 20, { align: 'center' });

      yPos = 35;
      doc.setTextColor(0, 0, 0);

      // Order Information
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, yPos, contentWidth / 2 - 5, 70, 'F');

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(37, 99, 235);
      doc.text('Order Information', margin + 5, yPos + 8);

      yPos += 15;
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'bold');
      doc.text('PO Number:', margin + 5, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(selectedPO.poNumber || 'N/A', margin + 35, yPos);

      yPos += 8;
      doc.setFont(undefined, 'bold');
      doc.text('Created Date:', margin + 5, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(formatDate(selectedPO.poCreatedDate), margin + 35, yPos);

      yPos += 8;
      doc.setFont(undefined, 'bold');
      doc.text('Delivery Date:', margin + 5, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(formatDate(selectedPO.deliveryDate), margin + 35, yPos);

      yPos += 8;
      doc.setFont(undefined, 'bold');
      doc.text('Payment Terms:', margin + 5, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(selectedPO.paymentTerms || 'N/A', margin + 35, yPos);

      yPos += 8;
      doc.setFont(undefined, 'bold');
      doc.text('Status:', margin + 5, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(getStatusLabel(selectedPO.status), margin + 35, yPos);

      // Shipping Information
      yPos = 43;
      const rightColX = pageWidth / 2 + 5;
      doc.setFillColor(241, 245, 249);
      doc.rect(rightColX, yPos - 8, contentWidth / 2 - 5, 70, 'F');

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(37, 99, 235);
      doc.text('Shipping Information', rightColX + 5, yPos);

      yPos += 7;
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'bold');
      doc.text('Location:', rightColX + 5, yPos);
      doc.setFont(undefined, 'normal');
      const location = selectedPO.shipTo_Location || 'N/A';
      doc.text(location.substring(0, 30), rightColX + 25, yPos);

      yPos += 8;
      doc.setFont(undefined, 'bold');
      doc.text('Address:', rightColX + 5, yPos);
      doc.setFont(undefined, 'normal');
      const address = selectedPO.shipTo_Address1 || 'N/A';
      doc.text(address.substring(0, 30), rightColX + 25, yPos);

      if (selectedPO.shipTo_Address2) {
        yPos += 6;
        doc.text(selectedPO.shipTo_Address2.substring(0, 30), rightColX + 25, yPos);
      }

      yPos += 8;
      doc.setFont(undefined, 'bold');
      doc.text('Contact:', rightColX + 5, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(selectedPO.shipTo_ContactPerson || 'N/A', rightColX + 25, yPos);

      yPos += 8;
      doc.setFont(undefined, 'bold');
      doc.text('Phone:', rightColX + 5, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(selectedPO.shipTo_ContactNo || 'N/A', rightColX + 25, yPos);

      yPos = 115;

      // Order Items Table
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(37, 99, 235);
      doc.text('Order Items', margin, yPos);
      yPos += 10;

      doc.setFillColor(37, 99, 235);
      doc.rect(margin, yPos - 5, contentWidth, 8, 'F');

      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('Item Description', margin + 2, yPos);
      doc.text('Qty', margin + 80, yPos);
      doc.text('Unit Price', margin + 100, yPos);
      doc.text('Disc%', margin + 130, yPos);
      doc.text('Total', margin + 150, yPos, { align: 'right' });

      yPos += 8;
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'normal');

      poDetails.forEach((item, index) => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }

        if (index % 2 === 0) {
          doc.setFillColor(248, 250, 252);
          doc.rect(margin, yPos - 4, contentWidth, 7, 'F');
        }

        const itemTotal = item.qty * item.unitPrice * (1 - item.detailDiscount / 100);
        const desc = item.itemDescription.length > 35 ? item.itemDescription.substring(0, 35) + '...' : item.itemDescription;

        doc.text(desc, margin + 2, yPos);
        doc.text(item.qty.toString(), margin + 85, yPos);
        doc.text(`₱${item.unitPrice.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`, margin + 100, yPos);
        doc.text(`${item.detailDiscount}%`, margin + 133, yPos);
        doc.text(`₱${itemTotal.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`, margin + 150, yPos, { align: 'right' });
        yPos += 7;
      });

      // Grand Total
      yPos += 5;
      doc.setFillColor(37, 99, 235);
      doc.rect(margin, yPos - 4, contentWidth, 12, 'F');

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(255, 255, 255);
      const grandTotal = calculatePOTotal(poDetails);
      doc.text('GRAND TOTAL:', margin + 5, yPos + 4);
      doc.text(`₱${grandTotal.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`, margin + 150, yPos + 4, { align: 'right' });

      doc.save(`PO_${selectedPO.poNumber}_${new Date().toISOString().split('T')[0]}.pdf`);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const getStatusLabel = (status) => {
    const labels = ['Draft', 'Pending', 'Approved', 'Rejected', 'Cancelled'];
    return labels[status] || 'Unknown';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      0: { label: 'Draft', color: 'bg-gray-100 text-gray-800' },
      1: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
      2: { label: 'Approved', color: 'bg-green-100 text-green-800' },
      3: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
      4: { label: 'Cancelled', color: 'bg-red-100 text-red-800' }
    };
    const statusInfo = statusMap[status] || { label: 'Unknown', color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getApprovalResponseBadge = (response) => {
    const responseMap = {
      0: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      1: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      2: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const responseInfo = responseMap[response] || responseMap[0];
    const Icon = responseInfo.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${responseInfo.color}`}>
        <Icon className="w-3 h-3" />
        {responseInfo.label}
      </span>
    );
  };

  const calculatePOTotal = (details) => {
    return details.reduce((total, item) => {
      const itemTotal = item.qty * item.unitPrice * (1 - item.detailDiscount / 100);
      return total + itemTotal;
    }, 0);
  };

  const stats = {
    total: poHeaders.length,
    approved: poHeaders.filter(po => po.status === 2).length,
    pending: poHeaders.filter(po => po.status === 1).length,
    filtered: filteredPOs.length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-slate-700 font-semibold">Loading PO Reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen from-slate-50 to-slate-100 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 border border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-600 rounded-xl">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-800">Purchase Order Reports</h1>
                <p className="text-slate-600 mt-1">View and manage all purchase orders</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={exportToExcel}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all shadow-md hover:shadow-lg font-medium"
              >
                <FileSpreadsheet className="w-5 h-5" />
                Excel
              </button>
              <button
                onClick={exportToPDF}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all shadow-md hover:shadow-lg font-medium"
              >
                <FileDown className="w-5 h-5" />
                PDF
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1 font-medium uppercase">Total POs</p>
                <p className="text-3xl font-bold text-slate-800">{stats.total}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <FileText className="h-7 w-7 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1 font-medium uppercase">Approved</p>
                <p className="text-3xl font-bold text-green-600">{stats.approved}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <TrendingUp className="h-7 w-7 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1 font-medium uppercase">Pending</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-xl">
                <Clock className="h-7 w-7 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1 font-medium uppercase">Filtered</p>
                <p className="text-3xl font-bold text-blue-600">{stats.filtered}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <Package className="h-7 w-7 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-slate-200">
          <div className="flex gap-4 items-center mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by PO number, location, or contact person..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${showFilters ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
            >
              <Filter className="w-5 h-5" />
              Filters
            </button>
          </div>

          {showFilters && (
            <div className="pt-4 border-t border-slate-200">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
              >
                <option value="all">All Status</option>
                <option value="0">Draft</option>
                <option value="1">Pending</option>
                <option value="2">Approved</option>
                <option value="3">Rejected</option>
                <option value="4">Cancelled</option>
              </select>
            </div>
          )}
        </div>

        {/* PO List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <tr>
                  <th
                    onClick={() => handleSort('poNumber')}
                    className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-800 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      PO Number
                      {getSortIcon('poNumber')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('poCreatedDate')}
                    className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-800 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Created Date
                      {getSortIcon('poCreatedDate')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('deliveryDate')}
                    className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-800 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Delivery Date
                      {getSortIcon('deliveryDate')}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase">Location</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase">Payment Terms</th>
                  <th
                    onClick={() => handleSort('status')}
                    className="px-6 py-4 text-left text-xs font-bold uppercase cursor-pointer hover:bg-blue-800 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Status
                      {getSortIcon('status')}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-bold uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredPOs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <Package className="w-16 h-16 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">No purchase orders found</p>
                    </td>
                  </tr>
                ) : (
                  filteredPOs.map((po, idx) => (
                    <tr key={po.id} className={`hover:bg-slate-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className="px-6 py-4">
                        <div className="font-bold text-blue-600">{po.poNumber}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700 font-medium">{formatDate(po.poCreatedDate)}</td>
                      <td className="px-6 py-4 text-sm text-slate-700 font-medium">{formatDate(po.deliveryDate)}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-800 font-semibold">{po.shipTo_Location}</div>
                        <div className="text-xs text-slate-600">{po.shipTo_ContactPerson}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700 font-medium">{po.paymentTerms}</td>
                      <td className="px-6 py-4">{getStatusBadge(po.status)}</td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleViewDetails(po)}
                          className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <PurchaseOrderModal
          order={selectedPO}
          onClose={() => {
            setShowModal(false);
            setSelectedPO(null);

          }}
          companies={companies}
          vendors={vendors}
        />
      )}
    </div>
  );
};

export default PO_Reports;